import { ProjectUser } from '@monorepo/shared/apiClient/types';
import { Input } from '@monorepo/shared/componentsV2/Input';
import { RadioGroupOption } from '@monorepo/shared/componentsV2/RadioGroup';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { permissionRoleLabels, ProjectPermissionRoles } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { useUpdateUserProjectRole } from '../hooks/useUpdateUserProjectRole';
import { formatUserNameWithEmail } from './inviteUserUtils';
import {
  UserSiteAccessFormValues,
  UserSiteAccessModal,
} from './UserSiteAccessModal';

interface ChangeUserRoleModalProps {
  projectId: string;
  user: ProjectUser;
  currentRole: ProjectPermissionRoles;
  roleOptions: RadioGroupOption[];
  isSelf: boolean;
  onClose: () => void;
}

export function ChangeUserRoleModal(props: ChangeUserRoleModalProps) {
  const { projectId, user, currentRole, roleOptions, isSelf, onClose } = props;

  const { showUserFriendlyErrorToast, success } = useToast();
  const [updateUserProjectRole] = useUpdateUserProjectRole({
    config: { throwOnError: true },
  });

  const onSubmit = useCallback(
    async (values: UserSiteAccessFormValues) => {
      try {
        await updateUserProjectRole({
          projectId,
          userId: user.userId,
          role: values.role,
        });

        const roleLabel = permissionRoleLabels[values.role];
        success(
          isSelf
            ? `Your role has been changed to ${roleLabel}.`
            : `${user.userName}'s role has been changed to ${roleLabel}.`,
        );

        onClose();
      } catch (err) {
        showUserFriendlyErrorToast(
          err,
          isSelf
            ? 'Unable to change your role.'
            : `Unable to change ${user.userName}'s role.`,
        );
      }
    },
    [
      isSelf,
      onClose,
      projectId,
      showUserFriendlyErrorToast,
      success,
      updateUserProjectRole,
      user,
    ],
  );

  return (
    <UserSiteAccessModal
      title="Change role"
      emailSlot={
        <Input
          label="Email"
          name="email"
          value={formatUserNameWithEmail(user.userName, user.userEmail)}
          disabled
          fullWidth
          dontSaveSpaceForErrorText
        />
      }
      roleOptions={roleOptions}
      initialValues={{ role: currentRole }}
      onSubmit={onSubmit}
      onClose={onClose}
      submitLabel="Update"
      submitPendingLabel="Updating..."
      disableWhenPristine
    />
  );
}
